import React, { useContext, useEffect, useState } from "react";
import {
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    OutlinedInput,
    FormHelperText,
    MenuItem,
    Select
} from "@mui/material";
import { ReactComponent as EditIcon } from './../../assets/Icons/edit.svg';
import { ReactComponent as ErrorIcon } from './../../assets/Icons/error.svg';

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ContributionReport } from "types/contribution-transaction";
import { useGetFundQuery } from "hooks/use-services/fund.service";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { isValidEmail, EMAIL_VALIDATION_ERROR_MESSAGE } from "utils/validate-email";

interface DonorInformationProps {
    contributionReport: ContributionReport,
    handleChange: (key: string, value: any) => void;
    newDonorDisabled: boolean;
}
export const DonorInformation: React.FC<
    DonorInformationProps
> = ({ contributionReport, handleChange, newDonorDisabled }) => {
    const { selectedAccount } = useContext<AuthData>(AuthContext);
    const { data: fundAccount } = useGetFundQuery(selectedAccount?.fundAccountId || 0, true)
    const [donors, setDonors] = useState<ContributionReport[]>([])
    const [donor, setDonor] = useState<number>(-1)
    const [edit, setEdit] = useState<boolean>(false)
    const [report, setReport] = useState<ContributionReport>({
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        donorEmail: ''
    })
    const [error, setError] = React.useState({
        name: false,
        addressLine1: false,
        city: false,
        state: false,
        zip: false,
        donorEmail: false
    })
    const [open, setOpen] = useState<boolean>(false)
    const [emailFormatError, setEmailFormatError] = useState<boolean>(false)
    useEffect(() => {
        if (fundAccount) {
            setDonors(() => {
                const users: ContributionReport[] = []
                if (fundAccount.entityInformation) {
                    const report = {
                        name: fundAccount.entityInformation?.entityName!,
                        addressLine1: fundAccount.entityInformation?.firstAddressLine!,
                        addressLine2: fundAccount.entityInformation?.secondAddressLine!,
                        city: fundAccount.entityInformation?.city!,
                        state: fundAccount.entityInformation?.state!,
                        zip: fundAccount.entityInformation?.zip!,
                        donorEmail: contributionReport.donorEmail
                    }
                    users.push(report)
                }
                fundAccount.fundAccountMembers?.forEach(item => {
                    const { user } = item

                    const report = {
                        name: `${user?.firstName} ${user?.lastName}`,
                        addressLine1: user?.addressLine1!,
                        addressLine2: user?.addressLine2!,
                        city: user?.city!,
                        state: user?.state!,
                        zip: user?.zip!,
                        donorEmail: user.email
                    }
                    users.push(report)

                })
                return users;
            })

        }
    }, [contributionReport.donorEmail, fundAccount]);
    const handleReportChange = (key: string, value: string) => {
        setError({
            ...error,
            [key]: false
        })
        if (key === 'donorEmail') {
            setEmailFormatError(value ? !isValidEmail(value) : false)
        }
        setReport({
            ...report,
            [key]: value
        })
    };
    const handleError = (key: string) => {
        setError({
            ...error,
            [key]: true
        })
    };
    const onSubmit = (event: any) => {
        event.preventDefault()
        event.stopPropagation()
        if (report.donorEmail && !isValidEmail(report.donorEmail)) {
            setEmailFormatError(true)
            return
        }
        handleChange('contributionReport', report)
        setOpen(false)
    }
    const selectDonor = (value: any) => {
        setDonor(value)
        const report = donors[value]
        setReport(report)
        setEmailFormatError(false)

    }
    const clear = () => {
        setDonor(-1)
        setEdit(false)
        setEmailFormatError(false)
        setReport({
            name: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            zip: '',
            donorEmail: ''
        })

    }
    return (
        <>
            <Dialog sx={{ m: 2 }} fullWidth={true}
                maxWidth={'sm'} open={open} onClose={() => setOpen(false)}>

                <DialogTitle sx={{ my: 2, pl: 4 }}> <Typography sx={{ color: "neutral" }} variant='subtitle1'> Enter donor information for the<br></br>
                    contribution receipt</Typography> </DialogTitle>
                <DialogContent sx={{ pl: 4 }}>
                    <Box
                        component="form"
                        autoComplete="off"
                        onSubmit={onSubmit}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={(donor !== -1) ? 8 : 12} sm={(donor !== -1) ? 8 : 12}>
                                <FormControl fullWidth>
                                    <Select
                                        fullWidth
                                        name="donor"
                                        placeholder="Select Donor or Entity for pre-fill"
                                        value={donor}
                                        onChange={(event) => selectDonor(event.target.value)}
                                    >
                                        {donors && donors.length > 0 && (
                                            donors.map((donor, index) => (
                                                <MenuItem key={index} value={index}>
                                                    {donor.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {
                                donor !== -1 &&
                                <Grid item xs={4} sm={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Button variant="text" color='primary' size='large' onClick={clear}  >
                                        Clear selection
                                    </Button>
                                </Grid>
                            }

                            <Grid item xs={12} sm={12}>

                                <FormControl error={error.name} fullWidth>
                                    <InputLabel shrink={false}>Name</InputLabel>
                                    <OutlinedInput name="name"
                                        disabled={donor !== -1 && !edit}
                                        value={report.name}
                                        onChange={(event) => handleReportChange("name", event.target.value)}
                                        onInvalid={(event) => handleError('name')}
                                        type="text" required />
                                    {error.name && <FormHelperText ><ErrorIcon />This field is required</FormHelperText>}

                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>

                                <FormControl error={error.donorEmail || emailFormatError} fullWidth>
                                    <InputLabel shrink={false}>Email*</InputLabel>
                                    <OutlinedInput name="donorEmail"
                                        disabled={donor !== -1 && !edit}
                                        value={report.donorEmail}
                                        onChange={(event) => handleReportChange("donorEmail", event.target.value)}
                                        onBlur={(event) => setEmailFormatError(event.target.value ? !isValidEmail(event.target.value) : false)}
                                        onInvalid={(event) => handleError('donorEmail')}
                                        type="text" required />
                                    {error.donorEmail && <FormHelperText ><ErrorIcon />This field is required</FormHelperText>}
                                    {!error.donorEmail && emailFormatError && <FormHelperText ><ErrorIcon />{EMAIL_VALIDATION_ERROR_MESSAGE}</FormHelperText>}

                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>

                                <FormControl error={error.addressLine1} fullWidth>
                                    <InputLabel shrink={false}>Address line 1*</InputLabel>
                                    <OutlinedInput name="addressLine1"
                                        disabled={donor !== -1 && !edit}
                                        value={report.addressLine1}
                                        onChange={(event) => handleReportChange("addressLine1", event.target.value)}
                                        onInvalid={(event) => handleError('addressLine1')}
                                        type="text" required />
                                    {error.addressLine1 && <FormHelperText ><ErrorIcon />This field is required</FormHelperText>}

                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>

                                <FormControl fullWidth>
                                    <InputLabel shrink={false}>Address line 2</InputLabel>
                                    <OutlinedInput name="addressLine2"
                                        disabled={donor !== -1 && !edit}
                                        value={report.addressLine2}
                                        onChange={(event) => handleReportChange("addressLine2", event.target.value)}
                                        type="text"
                                    />
                                </FormControl>

                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl error={error.city} fullWidth>
                                    <InputLabel shrink={false}>City*</InputLabel>
                                    <OutlinedInput name="city"
                                        disabled={donor !== -1 && !edit}
                                        value={report.city}
                                        onChange={(event) => handleReportChange("city", event.target.value)}
                                        onInvalid={(event) => handleError('city')}
                                        type="text" required />
                                    {error.city && <FormHelperText ><ErrorIcon />This field is required</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl error={error.state} fullWidth>
                                    <InputLabel shrink={false}>State*</InputLabel>
                                    <OutlinedInput name="state"
                                        disabled={donor !== -1 && !edit}
                                        value={report.state}
                                        onChange={(event) => handleReportChange("state", event.target.value)}
                                        onInvalid={(event) => handleError('state')}
                                        type="text" required />
                                    {error.state && <FormHelperText ><ErrorIcon />This field is required</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl error={error.zip} fullWidth>
                                    <InputLabel shrink={false}>ZIP* </InputLabel>
                                    <OutlinedInput name="zip"
                                        disabled={donor !== -1 && !edit}
                                        value={report.zip}
                                        onChange={(event) => handleReportChange("zip", event.target.value)}
                                        onInvalid={(event) => handleError('zip')}
                                        type="text" required />
                                    {error.zip && <FormHelperText ><ErrorIcon />This field is required</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                    {
                                        donor !== -1 && !edit &&
                                        <Button variant="outlined" color='primary' size='large' onClick={() => setEdit(true)}   >
                                            Edit information
                                        </Button>
                                    }

                                    <Box sx={{ flex: '1 1 auto' }} />
                                    <Button variant="text" color='primary' size='large' onClick={() => setOpen(false)} >
                                        Discard
                                    </Button>
                                    <Button variant="contained" color='primary' size='large' type="submit" disabled={emailFormatError}   >
                                        Save
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                </DialogContent>


            </Dialog>
            <Accordion defaultExpanded sx={{
                    p: {
                        xs: 0,
                        md: 3
                    }
                }}>
                <AccordionSummary tabIndex={-1}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="donor-info"
                    id="donor-info"
                >
                    <Typography sx={{ color: `neutral.main` }} variant="subtitle1">
                        Donor information for contribution receipt
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid sx={{ my: 1 }} container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle3" sx={{ color: `neutral.main` }}>
                                {contributionReport?.name}
                            </Typography>
                            <Typography variant='bodyCopyRegular' sx={{ color: `neutral.main` }} paragraph>
                                {contributionReport?.addressLine1},<br></br>
                                {
                                    (contributionReport?.addressLine2) && <>{contributionReport?.addressLine2} <br></br></>
                                }

                                {contributionReport?.city}, {contributionReport?.state}, {contributionReport?.zip}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Button className='underline' onClick={() => setOpen(true)} startIcon={<EditIcon />} variant='text' color="primary" disabled={newDonorDisabled} > Different donor? Click here </Button>
                        </Grid>



                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};
